/* @jsx h */
import { CustomerCheckout } from "@lemmonhq/types";
import { defineComponent, ref, h, computed, watch } from "vue";
import { useAutoFinalize } from "./useAutoFinalize";
import { useMenu } from "../../providers";
import {
  fetchCheckoutFamily,
  finalizeCustomerCheckouts,
} from "@app/server/client";
import {
  ErrorState,
  FinalizeButton,
  PendingCheckoutsList,
  SubmittedCheckoutsList,
  CompletedOrdersSection,
} from "./components";
import { debounce } from "@lemmonhq/orders";
import { pp } from "@lemmonhq/blogic";
import { Loading, useLocalization } from "@lemmonhq/uikit";

export const GroupedCheckoutStatus = defineComponent<{
  checkout: CustomerCheckout;
}>({
  props: ["checkout"],
  setup(props) {
    const { shop, realtime, groupOrder, cart, refetchGroupOrder } = useMenu();
    const { t } = useLocalization();
    const isLoading = ref(true);
    const error = ref<string | null>(null);
    const finalizeLoading = ref(false);

    // Separate confirmed (ready to finalize) vs completed (already finalized) checkouts
    const confirmedCheckouts = computed(() => {
      return (groupOrder.value?.checkouts || []).filter(
        (checkout) => checkout.status === "confirmed" && !checkout.order_id,
      );
    });

    const completedCheckouts = computed(() => {
      return (groupOrder.value?.checkouts || []).filter(
        (checkout) => checkout.status === "completed",
      );
    });
    // Handle finalize button click
    const handleFinalize = async () => {
      if (finalizeLoading.value) return;
      cancelAutoFinalize();
      try {
        finalizeLoading.value = true;

        // Get all confirmed checkout IDs (not already finalized)
        const checkoutIds = confirmedCheckouts.value.map(
          (checkout) => checkout.id,
        );

        if (checkoutIds.length === 0) {
          throw new Error(t("customer.checkout_status.no_checkouts_finalize"));
        }
        if (!groupOrder.value) {
          return alert(t("customer.checkout_status.unable_to_finalize"));
        }

        // Call the finalize API
        const result = await finalizeCustomerCheckouts({
          checkoutIds: checkoutIds,
          serviceMethod: groupOrder.value?.table_id
            ? "dine-in"
            : (cart.cart.serviceMethod as string),
          tableId: groupOrder.value.table_id || undefined,
          shopSlug: shop.value.slug,
          groupId: groupOrder.value?.id as string,
        });
        console.log("finalize result", result);
        await refetchGroupOrder();
      } catch (err: any) {
        error.value = err.message;
      } finally {
        finalizeLoading.value = false;
        autoFinalizeLoading.value = false;
        autoFinalizeCountdown.value = 0;
      }
    };

    // Create debounced fetchData function outside the watch
    const debouncedFetchData = debounce(async () => {
      await refetchGroupOrder();
      console.log("realtime.groupState changed");
    }, 2500);

    const {
      active: autoFinalizeLoading,
      countdown: autoFinalizeCountdown,
      cancel: cancelAutoFinalize,
    } = useAutoFinalize(
      () =>
        realtime.groupState.value.members.length === 1 &&
        confirmedCheckouts.value.length > 0 &&
        !finalizeLoading.value &&
        !error.value,
      handleFinalize,
    );

    // Refresh checkouts when realtime state changes
    watch(
      realtime.groupState,
      () => {
        console.log(
          "realtime.groupState",
          pp(realtime.groupState.value),
          pp(realtime.eventLog.value),
        );
        debouncedFetchData();
      },
      { deep: true },
    );

    // Auto-finalize notification component
    const AutoFinalizeNotification = () => (
      <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div class="bg-base-100 rounded-lg p-6 mx-4 max-w-md w-full shadow-2xl border border-primary/20">
          <div class="text-center space-y-4">
            <div class="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
              <span class="material-symbols-outlined text-3xl text-primary animate-pulse">
                schedule
              </span>
            </div>

            <div>
              <h3 class="text-xl font-semibold text-accent mb-2">
                {t("customer.checkout_status.auto_finalizing_order")}
              </h3>
              <p class="text-sm text-base-content/70 mb-4">
                {t("customer.checkout_status.auto_finalize_copy")}
              </p>
            </div>

            <div class="space-y-3">
              <div class="text-4xl font-bold text-accent tabular-nums">
                {autoFinalizeCountdown.value}
              </div>
              <div class="text-xs text-base-content/60">
                {t(
                  autoFinalizeCountdown.value === 1
                    ? "customer.checkout_status.finalizing_in_one"
                    : "customer.checkout_status.finalizing_in_many",
                  [autoFinalizeCountdown.value],
                )}
              </div>
            </div>

            <div class="flex gap-2 pt-2">
              <button
                class="btn btn-outline btn-sm flex-1"
                onClick={cancelAutoFinalize}
              >
                {t("customer.checkout_status.cancel")}
              </button>
              <button
                class="btn btn-primary btn-sm flex-1"
                onClick={handleFinalize}
              >
                {t("customer.checkout_status.finalize_now")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );

    return () => {
      if (error.value) {
        return (
          <ErrorState
            error={t("customer.checkout_status.error_loading", [error.value])}
          />
        );
      }
      if (!groupOrder.value?.checkouts) {
        <div class="flex-1 flex justify-center items-center">
          <Loading />
        </div>;
      }
      if (groupOrder.value?.checkouts) {
        return (
          <div class="">
            {/* Auto-finalize notification overlay */}
            {autoFinalizeLoading.value && <AutoFinalizeNotification />}

            <div class="container mx-auto p-4 max-w-7xl">
              {/* Desktop Layout */}
              <div class="hidden md:block">
                {/* Enhanced Finalize Button */}
                <FinalizeButton
                  onFinalize={handleFinalize}
                  isLoading={finalizeLoading.value}
                />

                {/* Modern Two Column Layout */}
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Confirmed Orders (Ready to Finalize) */}
                  <div>
                    <div class="py-4 space-y-1">
                      <h3 class="text-lg font-semibold text-base-content/70 flex items-center gap-2">
                        <span class="material-symbols-outlined text-success">
                          check_circle
                        </span>
                        {t("customer.checkout_status.confirmed_checkouts")}
                      </h3>
                      <SubmittedCheckoutsList />
                    </div>
                  </div>
                  {/* Right Column - Pending Orders */}
                  <div class="flex-1 flex flex-col">
                    <div class="py-4 space-y-1 flex flex-col flex-1">
                      <h3 class="text-lg font-semibold text-base-content/70 flex items-center gap-2">
                        <span class="material-symbols-outlined text-warning">
                          warning
                        </span>
                        {t("customer.checkout_status.pending_checkouts")}
                      </h3>
                      <PendingCheckoutsList />
                    </div>
                  </div>
                </div>

                {/* Completed Orders Section */}
                {completedCheckouts.value.length > 0 && (
                  <div class="mt-8">
                    <CompletedOrdersSection />
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Layout */}
            <div class="md:hidden">
              {/* Mobile Single Column Layout */}
              <div class="px-4 space-y-1">
                <h2 class="text-2xl text-left px-2 font-semibold text-secondary">
                  {t("customer.checkout_status.summary")}
                </h2>
              </div>
              <div class="space-y-6 pb-24">
                {/* Confirmed Orders (Ready to Finalize) */}
                <div class="p-4 space-y-1">
                  <h3 class="text-lg font-semibold text-base-content/70 flex items-center gap-2">
                    <span class="material-symbols-outlined text-success">
                      check_circle
                    </span>
                    {t("customer.checkout_status.your_orders")}
                  </h3>
                  <SubmittedCheckoutsList />
                </div>

                {/* Completed Orders */}
                {completedCheckouts.value.length > 0 && (
                  <div class="px-4 hidden">
                    <CompletedOrdersSection />
                  </div>
                )}
              </div>

              {/* Fixed Bottom Button for Mobile */}
              <div class="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-base-100 via-base-100 to-transparent border-t border-base-300/50 backdrop-blur-sm">
                <FinalizeButton
                  onFinalize={handleFinalize}
                  isLoading={finalizeLoading.value}
                />
              </div>
            </div>
          </div>
        );
      }
    };
  },
});
